
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import anime from "animejs";
import CaseStudyOverlay from "@/components/CaseStudyOverlay.vue";
import ScrollAnimatedTitle from "@/components/ScrollAnimatedTitle.vue";
import {
  categories,
  Category,
  CaseStudies,
  parseCaseStudies,
  caseStudiesApi,
} from "./caseStudies";

@Component({ components: { CaseStudyOverlay, ScrollAnimatedTitle } })
export default class Portfolio extends Vue {
  @Prop({ required: true, type: Number })
  progress!: number;
  animationStarted = false;
  activeStudy: Category | null = null;
  isDesktopLayout = false;
  portfolio = categories;
  studies: CaseStudies | null = null;
  studyIndex = 0;
  loading = false;
  loadError = "";
  private request: AbortController | null = null;

  get currentStudies() {
    return this.activeStudy ? this.studies?.[this.activeStudy] || [] : [];
  }

  get currentStudy() {
    return this.currentStudies[this.studyIndex] || null;
  }

  async loadStudies() {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.loading = true;
    this.loadError = "";
    this.studies = null;
    this.studyIndex = 0;
    const timeout = window.setTimeout(() => request.abort(), 10000);
    try {
      const response = await fetch(caseStudiesApi, {
        signal: request.signal,
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Content unavailable");
      const studies = parseCaseStudies(await response.json());
      if (this.request === request) this.studies = studies;
    } catch (_) {
      if (this.request === request)
        this.loadError = "We couldn’t load the case studies. Please try again.";
    } finally {
      window.clearTimeout(timeout);
      if (this.request === request) this.loading = false;
    }
  }

  created() {
    this.setLayoutMode();
  }

  mounted() {
    window.addEventListener("orientationchange", this.setLayoutMode);
    window.addEventListener("resize", this.setLayoutMode);
    this.$nextTick(() => this.syncHorizontalScroll(this.progress));
  }

  openVideo(id: Category | null) {
    this.activeStudy = id;
    this.studyIndex = 0;
    if (!id) {
      this.request?.abort();
      this.request = null;
    }
    if (id) {
      this.loadStudies();
      window.fbq &&
        window.fbq("track", "ViewContent", {
          content_category: "CaseStudy",
          content_name: id,
        });
    }
  }

  nextStudy() {
    if (this.studyIndex < this.currentStudies.length - 1) this.studyIndex++;
  }

  @Watch("progress")
  onProgressChange(val: number) {
    this.syncHorizontalScroll(val);

    if (val > 0.25 && !this.animationStarted) {
      this.animationStarted = true;
      anime({
        targets: this.$refs.tiles,
        opacity: [0, 1],
        translateX: [500, 0],
        duration: 2000,
        delay: (el, i) => i * 200,
        autoplay: true,
        // The final translateX(0) is visually redundant, but leaving it inline
        // keeps a transformed ancestor around every skewed tile. Safari can
        // then reuse a stale raster until hover forces a repaint.
        complete: () => {
          const tiles = this.$refs.tiles as HTMLElement[] | undefined;
          tiles?.forEach((tile) => tile.style.removeProperty("transform"));
        },
      });
    }
  }

  syncHorizontalScroll(progress: number) {
    const scroller = this.$refs.tilesScroller as HTMLElement | undefined;
    if (!scroller || !this.isDesktopLayout || !Number.isFinite(progress))
      return;

    // The 200vh slide pins its 100vh child for the second half of its runway.
    // Map only that pinned interval onto the strip's full horizontal travel.
    const pinnedProgress = Math.min(Math.max((progress - 0.5) * 2, 0), 1);
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    scroller.scrollLeft = pinnedProgress * maxScroll;
  }

  setLayoutMode() {
    this.isDesktopLayout = window.innerWidth >= 1400;
    this.$nextTick(() => this.syncHorizontalScroll(this.progress));
  }

  destroyed() {
    this.request?.abort();
    this.request = null;
    window.removeEventListener("orientationchange", this.setLayoutMode);
    window.removeEventListener("resize", this.setLayoutMode);
  }
}
